import React, { useState } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { Tenancy } from '../../types/tenancyTypes';

interface Props {
  tenancyId: string;
  subTenants?: Tenancy['subTenants'];
  onChanged?: () => void;
}

const TenancySubTenants: React.FC<Props> = ({
  tenancyId,
  subTenants = [],
  onChanged
}) => {
  const { currentUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!currentUser) return;
    setBusy(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/addSubTenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ tenancyId, name, email, phone })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to add sub-tenant');
      }
      setName('');
      setEmail('');
      setPhone('');
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (subTenantId: string) => {
    if (!currentUser) return;
    setBusy(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/removeSubTenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ tenancyId, subTenantId })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to remove sub-tenant');
      }
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3">Sub-tenants</h3>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      <div className="space-y-2 mb-4">
        {(subTenants || []).length === 0 && (
          <p className="text-sm text-gray-500">No sub-tenants yet.</p>
        )}
        {(subTenants || []).map((st) => (
          <div
            key={st.id}
            className="flex items-center justify-between text-sm border-b pb-2"
          >
            <div>
              <div className="font-medium text-gray-900">{st.name}</div>
              <div className="text-gray-600">
                {st.email}
                {st.phone ? ` • ${st.phone}` : ''}
              </div>
              <div className="text-gray-500">Status: {st.status}</div>
            </div>
            <div>
              {st.status === 'active' && (
                <button
                  onClick={() => handleRemove(st.id)}
                  disabled={busy}
                  className="text-red-600 hover:text-red-800 text-xs font-semibold"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="border rounded p-2 text-sm"
        />
        <input
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border rounded p-2 text-sm"
        />
        <input
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="border rounded p-2 text-sm"
        />
      </div>
      <button
        onClick={handleAdd}
        disabled={busy || !name || !email}
        className="mt-3 bg-blue-600 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Add Sub-tenant'}
      </button>
    </div>
  );
};

export default TenancySubTenants;
